import { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, DatePicker, Progress, ProgressProps, Space } from "antd";
import { LeftCircleFilled, SearchOutlined } from "@ant-design/icons";
import * as echarts from "echarts";
import moment from "moment";

import SimCardTrafficApi from "../../../../../api/SimCardTrafficApi";
import { ChartCard, ChartContainer, ChartsWrapper, ChartTitle, DateRangeWrapper, GoBackText, GoBackWrapper, InfoCard, InfoColumn, InfoLabel, InfoRow, InfoValue, SimCardTrafficDetailWrapper, TitleBar, TitleText, TrafficUsageText} from "./SimCardTrafficDetails.styled";
import StringUtils from "../../../../../util/StringUtils";
import SimCardTrafficUtils from "../../utils/SimCardTrafficUtils";
import dayjs from "dayjs";

const SimCardTrafficDetails: React.FC = () => {
    let { iccid } = useParams<{ iccid: string }>();

    let navigate = useNavigate();
    let location = useLocation();
    let simCardTrafficApi = new SimCardTrafficApi();

    let lineChartRef = useRef<HTMLDivElement>(null);
    let lineChartInstance = useRef<echarts.ECharts | null>(null);

    let [simCardTrafficView, setSimCardTrafficView] = useState<any>(null);
    let [historyData, setHistoryData] = useState<any[]>([]);
    let [loading, setLoading] = useState(true);

    let now = moment();
    let defaultStartTime = now.clone().subtract(1, 'month').startOf('day').valueOf();
    let defaultEndTime = now.clone().endOf('day').valueOf();
    let [startTime, setStartTime] = useState(defaultStartTime);
    let [endTime, setEndTime] = useState(defaultEndTime);

    let onGoBack = () => {
        if (location.key === 'default') {
            navigate("/sim-card-traffic");
        } else {
            navigate(-1);
        }
    };

    let getUsagePercent = (used: number, total: number): string => {
        if (!total || total === 0) return '0';
        return ((used / total) * 100).toFixed(1);
    };

    let loadSimCardTrafficDetail = async () => {
        if (!iccid) return;
        setLoading(true);

        let detailResponse = await simCardTrafficApi.getLatestSimCardTrafficView(iccid);
        let detail = detailResponse.data.data;
        setSimCardTrafficView(detail);

        loadHistoryData();
        setLoading(false);
    };

    let loadHistoryData = async () => {
        if (!iccid || !startTime || !endTime) return;

        let start = startTime;
        let end = endTime;
        let historyResponse = await simCardTrafficApi.queryHistorySimCardTraffics(iccid, start, end);
        let data = historyResponse.data.data;
        setHistoryData(data);
        renderLineChart(data);
    };

    let renderLineChart = (data: any[]) => {
        if (!lineChartRef.current) return;

        if (!lineChartInstance.current) {
            lineChartInstance.current = echarts.init(lineChartRef.current);
        }

        let formatTimestampAxis = (value)=> {
            let text = moment(Number(value)).format('YYYY/MM/DD HH:mm:ss');
            text=text.replace(' ', '\n');
            return text;
        }

        let seriesData = data.map(item => {
            return {
                name: item.timestamp + '',
                value: [Number(item.timestamp), Number(item.used), Number(item.total)],
            };
        });

        let option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    animation: false
                },
                confine: true,
                backgroundColor: "rgba(0,0,0,0.7)",
                formatter: function(params) {
                    if (params == null) {
                        return '';
                    }

                    let seriesContent = '';
                    for(let param of params) {
                        if (param.value==null) {
                            continue;
                        }

                        seriesContent += `
                            <div style="color: #EEEEEE;">
                                <div style="padding: 5px 0px;">
                                    <span style="display: inline-block; width: 70px;">流量使用:</span> 
                                    <span style="display: inline-block">${param.value[1].toFixed(1)}/${param.value[2].toFixed(1)} MB</span>
                                </div>
                                <div>
                                    <span style="display: inline-block; width: 70px;">时间:</span> 
                                    <span style="display: inline-block">${moment(param.value[0]).format('YYYY-MM-DD HH:mm:ss')}</span>
                                </div>
                            </div>
                        `;
                        break;
                    }
                    let content=`
                        <div style="width: 230px;">
                            ${seriesContent}
                        </div>
                    `;
                    return content;
                },
            },
            grid: {
                left: 20,
                right: 20,
                bottom: 40,
                top: 40,
                containLabel: true
            },
            xAxis: {
                type: 'category',
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true
                },
                splitLine: {
                    show: true,
                },
                splitNumber: 2,
                axisLabel: {
                    formatter: function (value, index) {
                        return formatTimestampAxis(value);
                    }
                },
            },
            yAxis: {
                type: 'value',
                name: '流量(MB)',
                boundaryGap: [0, '100%'],
                axisLine: {
                    show: true
                },
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true
                },
                splitLine: {
                    show: true,
                },
            },
            series: [{
                name: '流量使用',
                type: 'line',
                smooth: true,
                showSymbol: false,
                data: seriesData,
                lineStyle: {
                    color: '#1890ff',
                    width: 2
                },
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
                        { offset: 1, color: 'rgba(24, 144, 255, 0.05)' }
                    ])
                },
                itemStyle: {
                    color: '#1890ff'
                }
            }]
        };

        lineChartInstance.current.setOption(option);
    };

    let onQuery = () => {
        loadHistoryData();
    };

    useEffect(() => {
        loadSimCardTrafficDetail();

        let handleResize = () => {
            lineChartInstance.current?.resize();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            lineChartInstance.current?.dispose();
        };
    }, []);

    if (loading || !simCardTrafficView) {
        return (
            <SimCardTrafficDetailWrapper>
                <div style={{ textAlign: 'center', marginTop: '100px' }}>
                    加载中...
                </div>
            </SimCardTrafficDetailWrapper>
        );
    }
    
    // render
    let traffic = simCardTrafficView.latestSimCardTraffic;
    let used = traffic?.used || 0;
    let total = traffic?.total || 0;
    let usagePercent = parseFloat(getUsagePercent(used, total));

    const conicColors: ProgressProps['strokeColor'] = {
        '0%': 'rgba(22, 120, 255, 0.5)',
        '20%': 'rgba(22, 120, 255, 0.8)',
        '100%': 'rgba(200, 0, 0, 0.95)',
    };
    
    return (
        <SimCardTrafficDetailWrapper>
            <TitleBar>
                <GoBackWrapper onClick={onGoBack}>
                    <LeftCircleFilled style={{ fontSize: '20px' }} />
                    <GoBackText>返回</GoBackText>
                </GoBackWrapper>
                <TitleText>SIM卡流量详情 - {traffic?.iccid}</TitleText>
                <div style={{ width: '80px' }}></div>
            </TitleBar>

            <InfoCard>
                <InfoColumn>
                    <InfoRow>
                        <InfoLabel>ICCID</InfoLabel>
                        <InfoValue>{traffic?.iccid || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>状态</InfoLabel>
                        <InfoValue>{SimCardTrafficUtils.getStatusText(traffic?.status)}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>生效时间</InfoLabel>
                        <InfoValue>
                            {traffic?.startDate ? moment(parseFloat(traffic.startDate)).format("YYYY-MM-DD HH:mm:ss") : '-'}
                        </InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>过期时间</InfoLabel>
                        <InfoValue>
                            {traffic?.expireDate ? moment(parseFloat(traffic.expireDate)).format("YYYY-MM-DD HH:mm:ss") : '-'}
                        </InfoValue>
                    </InfoRow>
                </InfoColumn>
                <InfoColumn>
                    <InfoRow>
                        <InfoLabel>流量情况</InfoLabel>
                        <InfoValue>已使用: {used.toFixed(1)} / {total.toFixed(1)} MB ({usagePercent}%)</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>流量比例</InfoLabel>
                        <InfoValue>
                            <Progress
                                size="small"
                                status="active"
                                showInfo={true}
                                percentPosition={{ align: 'end', type: 'outer' }}
                                strokeColor={conicColors}
                                percent={usagePercent} 
                                format={(percent, successPercent)=> {
                                    return <TrafficUsageText>{usagePercent.toFixed(1)}%</TrafficUsageText>;
                                }}
                            />
                        </InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>流量套餐</InfoLabel>
                        <InfoValue>{traffic?.iratePlanName || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>更新时间</InfoLabel>
                        <InfoValue>{moment(parseFloat(traffic?.timestamp)).format("YYYY-MM-DD HH:mm:ss")}</InfoValue>
                    </InfoRow>
                </InfoColumn>

                <InfoColumn>
                    <InfoRow>
                        <InfoLabel>设备序列号</InfoLabel>
                        <InfoValue>{StringUtils.emptyToSlash(simCardTrafficView.tenantNode?.sn)}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>设备名称</InfoLabel>
                        <InfoValue>{StringUtils.emptyToSlash(simCardTrafficView.device?.name)}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>租户名称</InfoLabel>
                        <InfoValue>{StringUtils.emptyToSlash(simCardTrafficView.tenant?.name)}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>运营商</InfoLabel>
                        <InfoValue>{StringUtils.emptyToSlash(simCardTrafficView.tenantNode?.oper)}</InfoValue>
                    </InfoRow>
                </InfoColumn>
            </InfoCard>

            <ChartsWrapper>
                <ChartCard>
                    <ChartTitle>流量使用趋势</ChartTitle>
                    <DateRangeWrapper>
                        <DatePicker
                            showTime
                            value={dayjs(startTime)}
                            onChange={(date) => setStartTime(date.toDate().getTime())}
                            placeholder="开始时间"
                        />
                        <span style={{ color: '#999' }}>-</span>
                        <DatePicker
                            showTime
                            value={dayjs(endTime)}
                            onChange={(date) => setEndTime(date.toDate().getTime())}
                            placeholder="结束时间"
                        />
                        <Button type="primary" size="small" icon={<SearchOutlined />} onClick={onQuery} style={{marginLeft: '20px'}}>查询</Button>
                    </DateRangeWrapper>
                    <ChartContainer ref={lineChartRef} />
                </ChartCard>
            </ChartsWrapper>
        </SimCardTrafficDetailWrapper>
    );
};

export default SimCardTrafficDetails;